import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

const DEFAULT_POSITION = { x: 8, y: 44 };
const DRAG_THRESHOLD_PX = 3;
/** Panes whose shorter side is below this start with the list collapsed. */
const SMALL_PANE_PX = 240;

/** Position and collapsed state, kept inside the pane. */
export function useListPlacement(shown: boolean) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(DEFAULT_POSITION);
  const [smallPane, setSmallPane] = useState(false);
  const smallPaneRef = useRef(false);
  // The reader's own choice; cleared when the pane crosses the size threshold.
  const [collapsedChoice, setCollapsedChoice] = useState<boolean | null>(null);
  const collapsed = collapsedChoice ?? smallPane;
  const placement = { ...position, collapsed };

  const clamp = useCallback((x: number, y: number) => {
    const panel = panelRef.current;
    const pane = panel?.offsetParent as HTMLElement | null | undefined;
    if (!panel || !pane) return { x, y };
    return {
      x: Math.min(
        Math.max(0, x),
        Math.max(0, pane.clientWidth - panel.offsetWidth)
      ),
      y: Math.min(
        Math.max(0, y),
        Math.max(0, pane.clientHeight - panel.offsetHeight)
      ),
    };
  }, []);

  // Any header press may become a drag, the ruler button included, so the
  // collapsed icon stays movable; a press that moves swallows its click.
  const onDragStart = (e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const dx = startX - position.x;
    const dy = startY - position.y;
    let dragging = false;
    const move = (ev: PointerEvent) => {
      if (
        !dragging &&
        Math.abs(ev.clientX - startX) <= DRAG_THRESHOLD_PX &&
        Math.abs(ev.clientY - startY) <= DRAG_THRESHOLD_PX
      )
        return;
      dragging = true;
      const next = clamp(ev.clientX - dx, ev.clientY - dy);
      setPosition(next);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (!dragging) return;
      const swallow = (ev: MouseEvent) => {
        ev.stopPropagation();
        ev.preventDefault();
      };
      window.addEventListener('click', swallow, { capture: true, once: true });
      // No click follows when the pointer was released off the header.
      setTimeout(
        () => window.removeEventListener('click', swallow, { capture: true }),
        0
      );
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const toggleCollapsed = () => setCollapsedChoice(!collapsed);

  // Keep the list inside the pane (on pane or panel resize, e.g. expanding),
  // and collapse it in small panes.
  useEffect(() => {
    const panel = panelRef.current;
    const pane = panel?.offsetParent as HTMLElement | null | undefined;
    if (!shown || !panel || !pane || typeof ResizeObserver === 'undefined')
      return undefined;
    const observer = new ResizeObserver(() => {
      const small =
        Math.min(pane.clientWidth, pane.clientHeight) < SMALL_PANE_PX;
      if (small !== smallPaneRef.current) {
        smallPaneRef.current = small;
        setSmallPane(small);
        setCollapsedChoice(null);
      }
      setPosition((p) => clamp(p.x, p.y));
    });
    observer.observe(pane);
    observer.observe(panel);
    return () => observer.disconnect();
  }, [shown, clamp]);

  return { panelRef, placement, onDragStart, toggleCollapsed };
}
